import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import '../style.css';
import '../nov.css';

const PhysicalDistribution = () => {
  const services = [
    {
      title: "CD & Vinyl",
      description: "Professional pressing and manufacturing services for CDs, vinyl records, and cassettes"
    },
    {
      title: "Warehousing",
      description: "Secure storage solutions with climate-controlled facilities for your inventory"
    },
    {
      title: "Logistics",
      description: "Fast and reliable shipping to retailers, distributors, and customers worldwide"
    },
    {
      title: "Global Reach",
      description: "Access to major retail chains and independent stores across the globe"
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Production",
      description: "We manufacture your CDs, vinyl, or cassettes to the highest quality standards with professional duplication and packaging."
    },
    {
      number: 2,
      title: "Storage",
      description: "Your products are stored in our secure, climate-controlled warehouses, ready for immediate distribution."
    },
    {
      number: 3,
      title: "Distribution",
      description: "We ship your products to retailers, distributors, and customers with fast and reliable logistics partners worldwide."
    },
    {
      number: 4,
      title: "Tracking & Support",
      description: "Monitor your shipments in real-time and get dedicated support throughout the entire process."
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black py-20 lg:py-32 overflow-hidden">
        <div className="container mx-auto px-4 lg:px-0 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm badge-gradient text-black font-light py-2 px-4 rounded-full uppercase">
                Physical Distribution Services
              </span>
            </motion.div>

            <motion.h1 
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white" 
              style={{ fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              PHYSICAL MUSIC<br/>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#9BCFFF] via-[#FCD4FE] to-[#B6F1F6]">
                DISTRIBUTION
              </span>
            </motion.h1>

            <motion.p 
              className="text-xl text-gray-300 mb-8 font-light max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              From pressing to delivery, we handle your physical music distribution with care and professionalism.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-32 lg:py-48 bg-[#f5f5f7] relative overflow-hidden">
        <div className="container mx-auto px-4 lg:px-0 relative z-10">
          <div className="text-center mb-16">
            <motion.div 
              className="mb-10"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm badge-gradient text-black font-light py-2 px-4 rounded-full">
                Complete Distribution Solutions
              </span>
            </motion.div>

            <motion.h2 
              className="text-4xl md:text-7xl lg:text-8xl font-bold mb-6 text-center" 
              style={{ color: 'rgb(70, 52, 135)', fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              OUR SERVICES
            </motion.h2>

            <motion.p 
              className="md:text-xl font-light max-w-[577px] m-auto text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Professional physical distribution solutions for your music releases
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="group relative flex flex-col items-center justify-center rounded-2xl px-6 py-8 text-sm font-medium shadow-[inset_0_-8px_10px_#8fdfff1f] backdrop-blur-sm bg-white text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="absolute inset-0 block h-full w-full animate-gradient bg-gradient-to-r from-[#ffaa40]/50 via-[#9c40ff]/50 to-[#ffaa40]/50 bg-[length:300%_100%] p-[1px] ![mask-composite:subtract] [border-radius:inherit] [mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)]"></div>
                
                <div className="relative z-10 flex flex-col items-center">
                  <h3 className="text-lg font-bold text-black mb-2 uppercase" style={{ fontFamily: 'aesthet-nova' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-xs">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-32 lg:py-48 bg-gradient-to-br from-gray-900 to-black relative overflow-hidden">
        <div className="container mx-auto px-4 lg:px-0 relative z-10">
          <div className="text-center mb-16">
            <motion.div 
              className="mb-10"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm badge-gradient text-black font-light py-2 px-4 rounded-full">
                Streamlined Process
              </span>
            </motion.div>

            <motion.h2 
              className="text-4xl md:text-7xl lg:text-8xl font-bold mb-6 text-white" 
              style={{ fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              HOW IT WORKS
            </motion.h2>

            <motion.p 
              className="md:text-xl font-light max-w-2xl mx-auto text-gray-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Simple and streamlined process from production to delivery
            </motion.p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-6 group"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-[#9BCFFF] to-[#FCD4FE] flex items-center justify-center text-black font-bold text-xl">
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 uppercase" style={{ fontFamily: 'aesthet-nova' }}>
                      {step.title}
                    </h3>
                    <p className="text-gray-300">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 lg:py-48 bg-[#f5f5f7] relative overflow-hidden">
        <div className="container mx-auto px-4 lg:px-0 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2 
              className="text-4xl md:text-7xl font-bold mb-6" 
              style={{ color: 'rgb(70, 52, 135)', fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              READY TO GET STARTED?
            </motion.h2>

            <motion.p 
              className="md:text-xl font-light text-gray-600 mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Contact us today to discuss your physical distribution needs and get a custom quote
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link 
                to="/contact"
                className="inline-block px-8 py-4 bg-black text-white rounded-full font-light uppercase hover:scale-105 transition-transform duration-300"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default PhysicalDistribution;
